import { slugify } from 'src/general/helpers/slugify';
import { BodyType, CarFilterDataRow, FilterListPayload, Model } from 'src/types/CarFilters.types';
import { FilterValues } from 'src/types/CataloguePage.types';

import { FilterOption } from './PlpFilters.type';

export const filterRangeFormatting = [
  { filterName: 'price', min: FilterValues.CashMin, max: FilterValues.CashMax },
  { filterName: 'finance', min: FilterValues.FinanceMin, max: FilterValues.FinanceMax },
  { filterName: 'year', min: FilterValues.YearMin, max: FilterValues.YearMax },
  { filterName: 'km', min: FilterValues.KMMin, max: FilterValues.KMMax },
  { filterName: 'fuelcon', min: FilterValues.FuelConsumptionMin, max: FilterValues.FuelConsumptionMax },
  { filterName: 'tow', min: FilterValues.TowCapBrakedMin, max: FilterValues.TowCapBrakedMax },
];

const convertFilterObjectToMapItem = (filterObjects: FilterOption[]) => {
  const result = new Map<string, FilterOption>();

  filterObjects.forEach((item) => {
    result.set(item.slug, {
      value: item.value,
      filter: item.filter,
      slug: item.slug,
      label: item.label,
    });
  });

  return result;
};

export const mapMakeAndModelFilter = (makeAndModels: Model[]): FilterOption[] => {
  return makeAndModels
    .map((make) => {
      if (!make.models) {
        return {
          slug: slugify(`${make.label}`),
          value: make.key ?? '',
          filter: FilterValues.Make,
          label: make.label,
        };
      }
      return make.models.map((model) => {
        if (model.key === 'all') {
          return {
            slug: slugify(`${make.label}`),
            value: make.key ?? '',
            filter: FilterValues.Make,
            label: make.label,
          };
        }
        return {
          slug: slugify(`${make.label}-${model.label}`),
          value: model.key,
          filter: FilterValues.Model,
          label: `${make.label} ${model.label}`,
        };
      });
    })
    .flat()
    .filter((item) => {
      return item !== null;
    });
};

export const mapFilterDataRowFilter = (
  data: CarFilterDataRow[],
  filter: FilterValues,
  labelAppend?: string,
): FilterOption[] => {
  return data
    .map((item) => {
      if (item.key === 'all') {
        return null;
      }
      return {
        slug: slugify(item.label),
        value: item.key as string,
        filter: filter,
        label: labelAppend ? `${item.label} ${labelAppend}` : item.label,
      };
    })
    .flat()
    .filter((item) => {
      return item !== null;
    });
};

const mapColourFilter = (data: CarFilterDataRow[]): FilterOption[] => {
  return data
    .map((item) => {
      return {
        slug: slugify(item.label),
        value: item.key as string,
        filter: FilterValues.Colour,
        label: item.label,
      };
    })
    .flat();
};

export const mapBodyTypeFilter = (data: BodyType[]) => {
  return data
    .map((bodyType) => {
      if (!bodyType.sizes?.length) {
        return {
          slug: slugify(bodyType.label),
          value: bodyType.key,
          filter: FilterValues.BodyType,
          label: bodyType.label,
        };
      }
      if (bodyType.sizes?.length) {
        return [
          ...bodyType.sizes.map((size) => {
            if (size.key === 'all') {
              return null;
            }
            return {
              slug: slugify(`${size.label}-${bodyType.label}`),
              value: size.key,
              filter: FilterValues.BodySize,
              label: `${size.label} ${bodyType.label}`,
            };
          }),
          {
            slug: slugify(bodyType.label),
            value: bodyType.key,
            filter: FilterValues.BodyType,
            label: bodyType.label,
          },
        ];
      }
      return {
        slug: slugify(bodyType.label ?? ''),
        value: bodyType.key,
        filter: FilterValues.BodyType,
        label: bodyType.label,
      };
    })
    .flat()
    .filter((item) => {
      return item !== null;
    });
};

/** Range filters that expect numeric values only (e.g. tow-1000-2000). Rejects slugs like "tow-pack" (highlighted feature). */
const isNumericRangeValue = (value: string): boolean => {
  return /^\d+$/.test(value);
};

const mapFilterParamToRangeFilters = (
  filterString: string,
  minFilterLabel: FilterValues,
  maxFilterLabel: FilterValues,
): FilterOption[] => {
  const parts = filterString.split('-');

  if (filterString.includes('under')) {
    const value = parts[2];
    if (!value || !isNumericRangeValue(value)) {
      return [];
    }
    return [
      {
        filter: maxFilterLabel,
        value,
        slug: filterString,
      },
    ];
  }

  if (filterString.includes('from')) {
    const value = parts[2];
    if (!value || !isNumericRangeValue(value)) {
      return [];
    }
    return [
      {
        filter: minFilterLabel,
        value,
        slug: filterString,
      },
    ];
  }

  const [, ...values] = parts;

  if (values.length === 1) {
    const value = values[0];
    if (!isNumericRangeValue(value)) {
      return [];
    }
    return [
      {
        filter: minFilterLabel,
        value,
        slug: filterString,
      },
      {
        filter: maxFilterLabel,
        value,
        slug: filterString,
      },
    ];
  }

  if (values.length === 2) {
    const [minVal, maxVal] = values;
    if (!isNumericRangeValue(minVal) || !isNumericRangeValue(maxVal)) {
      return [];
    }
    return values.map((value, index) => ({
      filter: index === 0 ? minFilterLabel : maxFilterLabel,
      value,
      slug: filterString,
    }));
  }

  return [];
};

export const mapDriveTypeFilter = (data: CarFilterDataRow[]): FilterOption[] => {
  return data
    .map((item) => {
      return {
        slug: slugify(item.key as string),
        value: item.key as string,
        filter: FilterValues.DriveType,
        label: item.label,
      };
    })
    .flat();
};

export const generateFilterObjects = (filterList: FilterListPayload): FilterOption[] => {
  return [
    ...mapMakeAndModelFilter(filterList.makeAndModel),
    ...mapFilterDataRowFilter(filterList.seats, FilterValues.Seats),
    ...mapFilterDataRowFilter(filterList.doors, FilterValues.Doors),
    ...mapFilterDataRowFilter(filterList.transmission, FilterValues.Transmission),
    ...mapFilterDataRowFilter(filterList.fuelType, FilterValues.FuelType),
    ...mapBodyTypeFilter(filterList.bodyType),
    ...mapDriveTypeFilter(filterList.driveType),
    ...mapFilterDataRowFilter(filterList.highlightedFeatures, FilterValues.HighlightedFeatures),
    ...mapColourFilter(filterList.colour),
    ...mapFilterDataRowFilter(filterList.ancapSafetyRating, FilterValues.Ancap, 'ANCAP'),
    ...mapFilterDataRowFilter(filterList.co2Emissions, FilterValues.Co2emissions, 'CO2 emissions'),
  ];
};

export const getPlpFiltersMap = (filterList: FilterListPayload) => {
  return convertFilterObjectToMapItem(generateFilterObjects(filterList));
};

const removeSurroundingQuotes = (keyword: string) => {
  return keyword.replace(/^'(.*)'$/, '$1');
};

const isKeywordSearchFilter = (filterString: string) => {
  return filterString.startsWith("'") && filterString.endsWith("'");
};

export const mapFilterParamsToFormattedFilters = (
  filterParams: string,
  filterData: Map<string, FilterOption>,
): FilterOption[] => {
  if (!filterParams) {
    return [];
  }
  const filterItems = filterParams.split('_');
  return filterItems
    .map((filterItem) => {
      if (filterData.has(filterItem)) {
        return filterData.get(filterItem)!;
      }

      for (const rangeFilter of filterRangeFormatting) {
        if (filterItem.includes(rangeFilter.filterName)) {
          const rangeOptions = mapFilterParamToRangeFilters(filterItem, rangeFilter.min, rangeFilter.max);
          if (rangeOptions.length > 0) {
            return rangeOptions;
          }
          return [];
        }
      }

      if (filterItem === 'available') {
        return {
          filter: FilterValues.State,
          value: filterItem,
          slug: filterItem,
          label: filterItem,
        };
      }

      if (isKeywordSearchFilter(filterItem)) {
        const decodedKeyword = decodeURI(filterItem);
        return {
          filter: FilterValues.Keyword,
          value: removeSurroundingQuotes(decodedKeyword),
          slug: filterItem,
          label: decodedKeyword,
        };
      }

      return null;
    })
    .filter((item) => {
      return item !== null;
    })
    .flat();
};
